import Vue from 'vue'
// eslint-disable-next-line import/no-named-as-default
import StarterKit from '@tiptap/starter-kit'
import { Link as ExLink } from '@tiptap/extension-link'
import { Image as ExImage } from '@tiptap/extension-image'
import { Table as ExTable } from '@tiptap/extension-table'
import { TableCell as ExTableCell } from '@tiptap/extension-table-cell'
import { TableHeader as ExTableHeader } from '@tiptap/extension-table-header'
import { Blockquote as ExBlockQuote } from '@tiptap/extension-blockquote'
import { HardBreak as ExHardBreak } from '@tiptap/extension-hard-break'
import { TextStyle } from '@tiptap/extension-text-style'
import { TableRow } from '@tiptap/extension-table-row'
import { TextAlign } from '@tiptap/extension-text-align'
import { Editor, Extension, Mark, mergeAttributes } from '@tiptap/core'

const normalize = (str: string): string => {
  /**
   * Таблица соответствия имен элементов el-tiptap к именам элементам tiptap
   */
  const tags = {
    list_item: 'listItem',
    bullet_list: 'bulletList',
    table_header: 'tableHeader',
    table_cell: 'tableCell',
    table_row: 'tableRow',
    font_size: 'fontSize',
    font_type: 'fontFamily',
    text_color: 'textColor',
    ordered_list: 'orderedList',
    horizontal_rule: 'horizontalRule',
    hard_break: 'hardBreak'
  }

  Object.entries(tags).forEach(([key, value]) => {
    str = str.replaceAll(key, value)
  })
  return str
}

const TableCell = ExTableCell.extend({
  addAttributes () {
    return {
      colspan: {
        default: 1
      },
      rowspan: {
        default: 1
      },
      background: {
        default: null
      },
      colwidth: {
        default: null,
        parseHTML: (element) => {
          const colwidth = element.getAttribute('colwidth')
          const value = colwidth
            ? [parseInt(colwidth, 10)]
            : null

          return value
        }
      }
    }
  },

  renderHTML ({ HTMLAttributes }) {
    const style = HTMLAttributes.background ? `background-color: ${HTMLAttributes.background};` : ''
    delete HTMLAttributes.background
    return ['td', mergeAttributes(this.options.HTMLAttributes, { ...HTMLAttributes, style }), 0]
  }
})

const Table = ExTable.extend({
  renderHTML ({ HTMLAttributes }) {
    return ['div', { style: 'overflow-x: auto;' }, ['table', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), ['tbody', 0]]]
  }
})

const TableHeader = ExTableHeader.extend({
  addAttributes () {
    return {
      colspan: {
        default: 1
      },
      rowspan: {
        default: 1
      },
      background: {
        default: null
      },
      colwidth: {
        default: null,
        parseHTML: (element) => {
          const colwidth = element.getAttribute('colwidth')
          const value = colwidth
            ? [parseInt(colwidth, 10)]
            : null

          return value
        }
      }
    }
  },

  renderHTML ({ HTMLAttributes }) {
    const style = `${HTMLAttributes.background ? `background-color: ${HTMLAttributes.background};` : ''}; ${HTMLAttributes.colwidth ? `width: ${HTMLAttributes.colwidth[0]}px;` : ''};`
    delete HTMLAttributes.background
    return ['th', mergeAttributes(this.options.HTMLAttributes, { ...HTMLAttributes, style }), 0]
  }
})

const Link = ExLink.extend({
  addAttributes () {
    return {
      href: {
        default: null
      },
      openInNewTab: {
        default: true
      }
    }
  },
  renderHTML ({ HTMLAttributes }) {
    return ['a', mergeAttributes(this.options.HTMLAttributes, { ...HTMLAttributes, target: HTMLAttributes.openInNewTab ? '_blank' : '_self' }), 0]
  }
})

const Image = ExImage.extend({
  addAttributes () {
    return {
      src: {
        default: null
      },
      alt: {
        default: null
      },
      title: {
        default: null
      },
      width: {
        default: null
      },
      height: {
        default: null
      },
      display: {
        default: null
      }
    }
  },
  renderHTML ({ HTMLAttributes }) {
    const className = HTMLAttributes.display ? `image-view image-view--${HTMLAttributes.display}` : 'image-view'
    delete HTMLAttributes.display
    return ['span', { class: className }, ['div', { class: 'image-view__body' }, ['img', mergeAttributes(this.options.HTMLAttributes, { ...HTMLAttributes, class: 'image-view__body__image' })]]]
  }
})

const Indent = Extension.create({
  name: 'indent',

  addGlobalAttributes () {
    return [
      {
        types: [
          'heading',
          'paragraph'
        ],
        attributes: {
          indent: {
            default: null,
            parseHTML: element => element.getAttribute('data-indent') || null,
            renderHTML: (attributes) => {
              if (attributes.indent === null) {
                return {}
              }
              return { 'data-indent': attributes.indent }
            }
          }
        }
      }
    ]
  }

})

const TextColor = Mark.create({
  name: 'textColor',

  parseHTML () {
    return [
      {
        tag: 'span[style]'
      }
    ]
  },

  renderHTML ({ HTMLAttributes, mark }) {
    const color = mark.attrs.color || ''
    HTMLAttributes.style = `color: ${color}`
    return ['span', HTMLAttributes, 0]
  },

  addAttributes () {
    return {
      color: {
        default: null,
        parseHTML: element => element.style.color,
        renderHTML: (attributes) => {
          if (!attributes.color) { return {} }
          return {
            style: `color: ${attributes.color}`
          }
        }
      }
    }
  }

})
const Underline = Mark.create({
  name: 'underline',

  parseHTML () {
    return [
      {
        tag: 'span[style]'
      }
    ]
  },

  renderHTML ({ HTMLAttributes }) {
    return ['u', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0]
  },

  addAttributes () {
    return {
      color: {
        default: null,
        parseHTML: element => element.style.color,
        renderHTML: (attributes) => {
          if (!attributes.color) { return {} }
          return {
            style: `color: ${attributes.color}`
          }
        }
      }
    }
  }

})
const FontSize = Mark.create({
  name: 'fontSize',

  parseHTML () {
    return [
      {
        tag: 'span',
        style: 'font-size'
      }
    ]
  },

  renderHTML ({ HTMLAttributes, mark }) {
    const fontSize = mark.attrs.px || '15'
    HTMLAttributes.style = `font-size: ${fontSize}px`
    return ['span', HTMLAttributes, 0]
  },

  addAttributes () {
    return {
      px: {
        default: null,
        parseHTML: element => element.style.fontSize,
        renderHTML: (attributes) => {
          if (!attributes.px) { return {} }
          return {
            style: `font-size: ${attributes.px}px`
          }
        }
      }
    }
  }
})

const FontFamily = Mark.create({
  name: 'fontFamily',

  parseHTML () {
    return [
      {
        tag: 'span',
        style: 'font-family'
      }
    ]
  },

  renderHTML ({ HTMLAttributes, mark }) {
    const fontFamily = mark.attrs.name
    HTMLAttributes.style = `font-family: ${fontFamily}`
    return ['span', HTMLAttributes, 0]
  },

  addAttributes () {
    return {
      px: {
        default: null,
        parseHTML: element => element.style.fontFamily?.replace(/['"]+/g, ''),
        renderHTML: (attributes) => {
          if (!attributes.fontFamily) { return {} }

          return {
            style: `font-family: ${attributes.fontFamily}`
          }
        }
      }
    }
  }
})

export default Vue.extend({
  data () {
    return {
      editor: null,
      detailText: null
    }
  },
  mounted () {
    this.editor = new Editor({
      editable: false,
      content: this.content ? JSON.parse(normalize(this.content)) : '',
      extensions: [
        StarterKit,
        Link,
        Table,
        TableCell,
        TableRow,
        TableHeader,
        Image,
        FontSize,
        TextColor,
        Indent,
        Underline,
        ExBlockQuote,
        ExHardBreak,
        TextAlign.configure({
          types: ['heading', 'paragraph']
        }),
        TextStyle,
        FontFamily
      ]
    })
  }
})
